import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
  Spinner
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import moment from "moment";

const API_URL = "https://dlaviapi.stechomeyazilim.info/Tips";

const TipsList = () => {
  document.title = "Günün İpucu Listesi | DLApass ADMIN";

  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTip, setCurrentTip] = useState(null);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);

  // --- API İŞLEMLERİ (CRUD) ---

  // 1. Verileri Çekme (Read)
  const fetchTips = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      const json = await response.json();
      setTips(json.value || []);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
    } finally {
      setLoading(false);
    }
  };

  // 2. Yeni Ekleme (Create)
  const handleAddTip = async (values) => {
    try {
      const payload = {
        Tip_Text: values.tipText,
        IsActive: isActive,
        Created_DateTime: new Date().toISOString(),
      };

      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        fetchTips(); // Listeyi yenile
        toggle();
      }
    } catch (error) {
      console.error("Ekleme hatası:", error);
    }
  };

  // 3. Güncelleme (Update)
  const handleUpdateTip = async (values) => {
    try {
      const payload = {
        Tip_Text: values.tipText,
        IsActive: isActive,
      };

      // OData yapısına uygun ID gönderimi: /Tips(5)
      const response = await fetch(`${API_URL}(${currentTip.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        fetchTips(); // Listeyi yenile
        toggle();
      }
    } catch (error) {
      console.error("Güncelleme hatası:", error);
    }
  };

  // 4. Silme (Delete)
  const handleDeleteTip = async () => {
    try {
      const response = await fetch(`${API_URL}(${currentTip.ID})`, {
        method: "DELETE",
      });

      if (response.ok) {
        fetchTips(); // Listeyi yenile
        setDeleteModal(false);
      }
    } catch (error) {
      console.error("Silme hatası:", error);
    }
  };

  // --- FORM VE MODAL YÖNETİMİ ---

  useEffect(() => {
    fetchTips();
  }, []);

  const toggle = () => {
    if (modal) {
      setModal(false);
      setCurrentTip(null);
      setIsEdit(false);
    } else {
      setModal(true);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      tipText: (currentTip && currentTip.Tip_Text) || "",
    },
    validationSchema: Yup.object({
      tipText: Yup.string().required("Lütfen bir ipucu metni giriniz."),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        handleUpdateTip(values);
      } else {
        handleAddTip(values);
      }
    },
  });

  const handleEditClick = (tip) => {
    setCurrentTip(tip);
    setIsEdit(true);
    setIsActive(tip.IsActive);
    setModal(true);
  };

  const onClickDelete = (tip) => {
    setCurrentTip(tip);
    setDeleteModal(true);
  };

  // --- TABLO KOLONLARI ---
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "ID", filterable: false },
      {
        Header: "İpucu Metni",
        accessor: "Tip_Text",
        filterable: true,
        Cell: (cell) => <div style={{ whiteSpace: "normal", minWidth: "250px" }}>{cell.value}</div>,
      },
      {
        Header: "Durum",
        accessor: "IsActive",
        filterable: true,
        Cell: (cell) => (
          <div className={`badge font-size-12 ${cell.value ? "bg-success" : "bg-danger"}`}>
            {cell.value ? "Aktif" : "Pasif"}
          </div>
        ),
      },
      {
        Header: "Kayıt Tarihi",
        accessor: "Created_DateTime",
        Cell: (cell) => moment(cell.value).format("DD.MM.YYYY HH:mm"),
      },
      {
        Header: "İşlemler",
        Cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link to="#" className="text-success" onClick={() => handleEditClick(cellProps.row.original)}>
              <i className="mdi mdi-pencil font-size-18" />
            </Link>
            <Link to="#" className="text-danger" onClick={() => onClickDelete(cellProps.row.original)}>
              <i className="mdi mdi-delete font-size-18" />
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTip}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Sistem" breadcrumbItem="Günün İpucu Listesi" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="text-sm-end mb-2">
                    <button type="button" className="btn btn-primary btn-rounded mb-2 me-2" onClick={toggle}>
                      <i className="mdi mdi-plus me-1" /> Yeni İpucu Ekle
                    </button>
                  </div>

                  {loading ? (
                    <div className="text-center my-5">
                    <Spinner color="primary" />
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={tips}
                      isGlobalFilter={true}
                      customPageSize={10}
                    />
                  )}

                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle} tag="h4">
                      {isEdit ? "İpucu Düzenle" : "İpucu Ekle"}
                    </ModalHeader>
                    <ModalBody>
                      <Form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
                        <Row>
                          <Col xs={12}>
                            <div className="mb-3">
                              <Label>İpucu Metni (İngilizce)</Label>
                              <Input
                                name="tipText"
                                type="textarea"
                                rows="5"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.tipText || ""}
                                invalid={validation.touched.tipText && !!validation.errors.tipText}
                              />
                              <FormFeedback>{validation.errors.tipText}</FormFeedback>
                            </div>

                            <div className="mb-3">
                              <Label className="d-block">Durum</Label>
                              <div className="form-check form-switch form-switch-md">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="activeSwitch"
                                  checked={isActive}
                                  onChange={() => setIsActive(!isActive)}
                                />
                                <label className="form-check-label" htmlFor="activeSwitch">
                                  {isActive ? "Aktif" : "Pasif"}
                                </label>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <div className="text-end">
                          <button type="submit" className="btn btn-success">
                            {isEdit ? "Güncelle" : "Kaydet"}
                          </button>
                        </div>
                      </Form>
                    </ModalBody>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(TipsList);